import { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import TiptapLink from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import Highlight from '@tiptap/extension-highlight'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableCell } from '@tiptap/extension-table-cell'
import { TableHeader } from '@tiptap/extension-table-header'
import { api } from '../lib/api'
import { GlowCard } from '../components/ui/GlowCard'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { useToast } from '../components/ui/Toast'

interface Article {
  id: string
  slug: string
  title: string
  content: string | null
  article_type: string
  category_id: string | null
  category_name: string | null
  visibility: string
  status: string
  tags: string | null
  view_count: number
  version: number
  updated_at: string
  sections?: Array<{ id: string; title: string; content: string | null; section_type: string; children?: Array<{ title: string; content: string | null }> }>
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days}d ago`
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function KBArticlePage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const [article, setArticle] = useState<Article | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const { toast } = useToast()

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      TiptapLink.configure({ openOnClick: true }),
      Image,
      TaskList,
      TaskItem.configure({ nested: true }),
      Highlight,
      Table.configure({ resizable: false }),
      TableRow,
      TableCell,
      TableHeader,
    ],
    editable: false,
  })

  useEffect(() => {
    if (!slug) return
    api.get<Article>(`/kb/articles/${slug}`)
      .then(res => {
        setArticle(res)

        if (editor) {
          // Try loading content into TipTap
          let loaded = false

          // 1. Check for tiptap_content
          if ((res as any).tiptap_content) {
            try {
              editor.commands.setContent(JSON.parse((res as any).tiptap_content))
              loaded = true
            } catch { /* fallthrough */ }
          }

          // 2. Merge sections (legacy format)
          if (!loaded && res.sections && res.sections.length > 0) {
            const htmlParts: string[] = []
            for (const s of res.sections) {
              htmlParts.push(`<h2>${s.title}</h2>`)
              if (s.content) {
                const cleaned = s.content.replace(/\\n/g, '\n')
                const paragraphs = cleaned.split('\n\n').filter(Boolean)
                for (const p of paragraphs) {
                  htmlParts.push(`<p>${p.replace(/\n/g, '<br>')}</p>`)
                }
              }
              if (s.children) {
                for (const child of s.children) {
                  htmlParts.push(`<h3>${child.title}</h3>`)
                  if (child.content) {
                    const cleaned = child.content.replace(/\\n/g, '\n')
                    htmlParts.push(`<p>${cleaned.replace(/\n/g, '<br>')}</p>`)
                  }
                }
              }
            }
            if (htmlParts.length > 0) {
              editor.commands.setContent(htmlParts.join(''))
              loaded = true
            }
          }

          // 3. Fallback: raw content
          if (!loaded && res.content) {
            editor.commands.setContent(res.content)
          }
        }
      })
      .catch(() => setError('Article not found'))
      .finally(() => setLoading(false))
  }, [slug, editor])

  function copyLink() {
    // Use the public URL for sharing
    const publicUrl = `${window.location.origin}/help/${article?.slug || slug}`
    navigator.clipboard.writeText(publicUrl)
    toast('Public link copied')
  }

  function copyShortLink() {
    const shortId = article?.id?.slice(0, 8) || slug?.slice(0, 8)
    const shortUrl = `${window.location.origin}/h/${shortId}`
    navigator.clipboard.writeText(shortUrl)
    toast('Short link copied')
  }

  if (loading) {
    return (
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <div className="skeleton" style={{ height: 32, width: 400, marginBottom: 16 }} />
        <div className="skeleton" style={{ height: 400 }} />
      </div>
    )
  }

  if (error || !article) {
    return (
      <div style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center', padding: '48px 24px' }}>
        <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.3 }}>📄</div>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-1)', marginBottom: 4 }}>Article not found</h2>
        <p style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 16 }}>This article may have been moved or deleted.</p>
        <Button variant="secondary" onClick={() => navigate('/kb')}>Back to Knowledge Base</Button>
      </div>
    )
  }

  const tags = article.tags ? article.tags.split(',').map(t => t.trim()).filter(Boolean) : []

  return (
    <div className="page-enter" style={{ maxWidth: 720, margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 20,
      }}>
        <Link to="/kb" style={{ color: 'var(--accent)', fontSize: 13, textDecoration: 'none', fontFamily: 'var(--font)' }}>
          &larr; Knowledge Base
        </Link>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="ghost" size="sm" onClick={copyShortLink} title="Copy short link">
            Short Link
          </Button>
          <Button variant="ghost" size="sm" onClick={copyLink} title="Copy public link">
            Share
          </Button>
          <Button variant="secondary" size="sm" onClick={() => navigate(`/kb/${article.slug}/edit`)}>
            Edit
          </Button>
        </div>
      </div>

      {/* Title */}
      <h1 style={{ fontSize: 28, fontWeight: 700, color: 'var(--text-1)', marginBottom: 8, fontFamily: 'var(--font)' }}>
        {article.title}
      </h1>

      {/* Meta */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 24, fontSize: 12, color: 'var(--text-3)', flexWrap: 'wrap' }}>
        <Badge v="default" sm>{article.article_type.replace('_', ' ').toUpperCase()}</Badge>
        {article.category_name && <span>{article.category_name}</span>}
        <span>&middot;</span>
        <Badge v={article.visibility === 'public' ? 'ok' : article.visibility === 'client' ? 'info' : 'default'} sm>
          {article.visibility}
        </Badge>
        <span>&middot;</span>
        <span>Updated {formatRelative(article.updated_at)}</span>
        <span>&middot;</span>
        <span style={{ fontFamily: 'var(--mono)' }}>{article.view_count} views</span>
        {tags.map(tag => (
          <Badge key={tag} v="default" sm>{tag}</Badge>
        ))}
      </div>

      {/* Content */}
      <GlowCard style={{ padding: '24px 28px' }}>
        <div className="kb-tiptap-editor" style={{ minHeight: 200 }}>
          <EditorContent editor={editor} />
        </div>
      </GlowCard>

      {/* Helpful */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 24, padding: 16 }}>
        <span style={{ fontSize: 13, color: 'var(--text-2)' }}>Was this helpful?</span>
        <Button variant="ghost" size="sm" onClick={() => toast('Thanks for the feedback!')}>👍 Yes</Button>
        <Button variant="ghost" size="sm" onClick={() => toast('Thanks — we\'ll improve this article')}>👎 No</Button>
      </div>

      {/* TipTap styles (same as editor) */}
      <style>{`
        .kb-tiptap-editor { outline: none; font-family: var(--font); font-size: 15px; line-height: 1.7; color: var(--text-1); }
        .kb-tiptap-editor h1 { font-size: 24px; font-weight: 700; margin: 24px 0 8px; }
        .kb-tiptap-editor h2 { font-size: 20px; font-weight: 600; margin: 20px 0 6px; }
        .kb-tiptap-editor h3 { font-size: 16px; font-weight: 600; margin: 16px 0 4px; }
        .kb-tiptap-editor p { margin: 0 0 8px; }
        .kb-tiptap-editor ul, .kb-tiptap-editor ol { padding-left: 24px; margin: 0 0 8px; }
        .kb-tiptap-editor blockquote { border-left: 3px solid var(--accent); padding-left: 16px; margin: 12px 0; color: var(--text-2); font-style: italic; }
        .kb-tiptap-editor pre { background: var(--bg-0); border: 1px solid var(--border); border-radius: 8px; padding: 16px; font-family: var(--mono); font-size: 13px; overflow-x: auto; margin: 12px 0; }
        .kb-tiptap-editor code { background: var(--bg-3); padding: 2px 6px; border-radius: 4px; font-family: var(--mono); font-size: 13px; }
        .kb-tiptap-editor pre code { background: none; padding: 0; }
        .kb-tiptap-editor img { max-width: 100%; border-radius: 8px; margin: 12px 0; }
        .kb-tiptap-editor a { color: var(--accent); text-decoration: underline; }
        .kb-tiptap-editor table { border-collapse: collapse; width: 100%; margin: 12px 0; }
        .kb-tiptap-editor th, .kb-tiptap-editor td { border: 1px solid var(--border); padding: 8px 12px; text-align: left; font-size: 13px; }
        .kb-tiptap-editor th { background: var(--bg-3); font-weight: 600; }
        .kb-tiptap-editor mark { background: var(--accent-m); color: var(--text-1); padding: 1px 4px; border-radius: 2px; }
      `}</style>
    </div>
  )
}
